// ProjectSwitcher — the project context control (LEFT of the domains, beside the
// cube). Shows the current project's display name as the always-visible
// operational anchor; the caret opens the list — the viewer's projects on the
// ONE project row (`projectRows`: the dot · the name · "<type> · <n> active
// sites" · a chip when blocked or on trial · the current ticked), the current
// first then A–Z, the family's search box past 8 (projects ONLY — the platform
// door is the gear's). Switching is a <Link> — project
// context lives in the URL, never the session (ADR-016).
//
// Context variants:
//   - Project context: the project name + caret (the operational anchor). The
//     demo/trial signals live in the ProjectBanner now, not a pill here.
//   - Platform context (`/platform/*`): the trigger is the RubixEMS wordmark +
//     caret (the cube beside it, in the Navbar, is the brand/home link); it
//     opens the same list.
//
// When the user has nothing to switch to (exactly one project), the control is
// a static label — no caret, no menu; on the platform with no project at all
// (a fresh deployment) the wordmark stands alone.
//
// The list is the shared rule (`appearsInProjectList`): reachable rows enter;
// suspended / expired-trial rows carry a chip and route to /project-blocked on
// click (so they never silently vanish on a member); terminated projects are
// hidden everywhere but the platform portal. A sysadmin's list is non-terminated
// too (ADR-022), so they DO see blocked rows chipped, same as a member; entry to
// a blocked project is via the platform portal, not this list.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { icons } from '../ui/icons.js';
import { useMe } from '../../hooks/useMe.js';
import { Menu, MenuItem } from '../ui/Menu.js';
import { LogoWordmark } from '../ui/Logo.js';
import { SHORT_LIST } from '../../hooks/useOptions.js';
import { matchProjectRows, projectRows, ProjectRowFace } from './projectRows.js';

export function ProjectSwitcher({ projectId }: { projectId: string | null }) {
  const { t, i18n } = useTranslation(['nav', 'common']);
  const { data: me } = useMe();
  const location = useLocation();
  const [now] = useState(() => Date.now());
  const [text, setText] = useState('');
  const rows = useMemo(() => (me ? projectRows(me, projectId, now, t, i18n.language) : []), [me, projectId, now, t, i18n.language]);
  if (!me) return null;

  const isPlatform = location.pathname.startsWith('/platform');
  const current = projectId ? me.availableProjects.find((p) => p.id === projectId) : undefined;
  const shown = text ? matchProjectRows(rows, text) : rows;
  // The family's search box past 8 — the host owns the text and matches by the shared rules.
  const search = rows.length > SHORT_LIST ? { value: text, onChange: setText, placeholder: t('nav:switcher.search') } : undefined;

  // The menu body is identical across variants — the project rows on the one
  // face. React Aria closes the menu on select; items are real links (href) via
  // the RouterProvider, so no close-callback plumbing is needed.
  const menuItems = shown.map((row) => (
    <MenuItem key={row.id} href={row.to} textValue={row.name}>
      <span className="flex items-center gap-2.5">
        <ProjectRowFace row={row} />
      </span>
    </MenuItem>
  ));

  // Platform context — the context control is the RubixEMS wordmark + caret
  // (the cube beside it, in the Navbar, is the brand/home link). Clicking it
  // opens the same list as the project switcher. The wordmark's own role=img
  // provides the "RubixEMS" accessible name; the Menu adds the haspopup affordance.
  if (isPlatform) {
    // Nothing to open (no project yet — a fresh deployment): the wordmark alone, no caret, no empty menu.
    if (rows.length === 0) {
      return (
        <span className="inline-flex items-center gap-1.5 h-9 px-1.5">
          <LogoWordmark height={28} className="text-[var(--color-text-primary)]" />
        </span>
      );
    }
    return (
      <Menu
        label={t('nav:switcher.label')}
        search={search}
        renderTrigger={() => (
          // No aria-label: the LogoWordmark's role=img already names this
          // "RubixEMS"; React Aria adds the haspopup/expanded affordance.
          <button
            type="button"
            className="group inline-flex items-center gap-1.5 h-9 px-1.5 rounded-md
              hover:bg-[var(--color-bg-hover)] outline-none
              focus-ring"
          >
            <LogoWordmark height={28} className="text-[var(--color-text-primary)]" />
            <icons.caretDown aria-hidden size={14} className="shrink-0 text-[var(--color-text-secondary)]" />
          </button>
        )}
      >
        {menuItems}
      </Menu>
    );
  }

  const triggerLabel = current?.name ?? t('nav:switcher.label');

  // Nothing to switch to → a static label (no caret, no menu).
  const interactive = rows.length > 1;
  if (!interactive) {
    // No current project to name AND nothing to switch to (e.g. a single-blocked
    // member on the blocked page) → render nothing, not a dead "Switch project".
    // (In-project single-project still shows the project NAME, since current is set.)
    if (!current) return null;
    return (
      <div className="inline-flex items-center gap-1.5 max-w-[9rem] sm:max-w-[14rem] px-2 h-9 text-sm font-semibold text-[var(--color-text-primary)]">
        <span className="truncate">{triggerLabel}</span>
      </div>
    );
  }

  return (
    <Menu
      label={t('nav:switcher.label')}
      search={search}
      renderTrigger={() => (
        <button
          type="button"
          aria-label={triggerLabel}
          className="inline-flex items-center gap-1.5 max-w-[9rem] sm:max-w-[14rem] px-2 h-9 rounded-md
            text-sm font-semibold text-[var(--color-text-primary)]
            hover:bg-[var(--color-bg-hover)] outline-none
            focus-ring"
        >
          {/* The project NAME is the always-visible operational context (text-first;
              the ambient theme carries the brand hue). Truncates on narrow rather
              than collapsing — you must always know which project you're in. */}
          <span className="truncate">{triggerLabel}</span>
          <icons.caretDown aria-hidden size={14} className="shrink-0 text-[var(--color-text-secondary)]" />
        </button>
      )}
    >
      {menuItems}
    </Menu>
  );
}
